import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydButton from '../../../commonDesign/Button'
import SydInput from '../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../commonDesign/SydLabel'
import SydCheckbox from '../../../commonDesign/SydCheckbox'
import SydAutocomplete from '../../../commonDesign/SydAutocomplete'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import {
  useCreateDelegatedIdentity,
  useUpdateDelegatedIdentity,
  useListEligibleIdentityUsers
} from '../../../../api/delegatedIdentities'
import { decipherError } from '../../../../utils/decipherError'

const CODE_PATTERN = /^[a-z0-9][a-z0-9-]*$/

function formatUserLabel (user) {
  const name = [user.lastName, user.firstName].filter(Boolean).join(', ')
  return name ? `${name} (${user.userId})` : `User ${user.userId}`
}

function buildUserOption (existing) {
  if (!existing?.userId) return null
  const name = [existing.userLastName, existing.userFirstName].filter(Boolean).join(', ')
  return {
    value: existing.userId,
    label: name ? `${name} (${existing.userId})` : `User ${existing.userId}`
  }
}

function DelegatedIdentityForm ({ identityId, existing, onCancel, onComplete }) {
  const isEdit = !!identityId
  const { data: eligibleUsers, isLoading: isLoadingUsers } = useListEligibleIdentityUsers()

  const { mutateAsync: create } = useCreateDelegatedIdentity()
  const { mutateAsync: update } = useUpdateDelegatedIdentity()

  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  const userOptions = useMemo(() =>
    (eligibleUsers ?? []).map((u) => ({
      value: u.userId,
      label: formatUserLabel(u)
    })),
  [eligibleUsers])

  const initialUserOption = useMemo(() => {
    if (!isEdit || !existing) return null
    return userOptions.find((o) => o.value === existing.userId) ?? buildUserOption(existing)
  }, [isEdit, existing, userOptions])

  const form = useForm({
    defaultValues: isEdit && existing
      ? {
        code: existing.code ?? '',
        displayName: existing.displayName ?? '',
        description: existing.description ?? '',
        userId: initialUserOption,
        enabled: !!existing.enabled
      }
      : {
        code: '',
        displayName: '',
        description: '',
        userId: null,
        enabled: true
      }
  })

  const [userSearchText, setUserSearchText] = useState('')

  const filteredUserOptions = useMemo(() => {
    if (!userSearchText) return userOptions
    const lower = userSearchText.toLowerCase()
    return userOptions.filter((opt) => opt.label.toLowerCase().includes(lower))
  }, [userOptions, userSearchText])

  const handleSubmit = useCallback(async (values) => {
    const payload = {
      ...values,
      userId: values.userId?.value ?? null
    }

    try {
      setProcessing(true)
      setError(null)
      if (isEdit) {
        await update({ delegatedIdentityId: Number(identityId), ...payload })
      } else {
        await create(payload)
      }
      onComplete()
    } catch (err) {
      setError(decipherError(err))
      setProcessing(false)
    }
  }, [isEdit, identityId, create, update, onComplete])

  const onSubmit = useCallback(async (e) => {
    const handler = form.handleSubmit(handleSubmit)
    await handler(e)
  }, [form, handleSubmit])

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Controller
            control={form.control}
            name='displayName'
            rules={{ required: 'Display name is required' }}
            render={(f) => (
              <SydLabel label='Display Name' required error={hookFormErrorAdapter(form, f.fieldState)}>
                <SydInput
                  value={f.field.value}
                  onChange={f.field.onChange}
                  placeholder='e.g. Nightly Operations'
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            control={form.control}
            name='code'
            rules={{
              required: 'Code is required',
              pattern: { value: CODE_PATTERN, message: 'Lowercase alphanumeric and hyphens only' }
            }}
            render={(f) => (
              <SydLabel label='Code' required error={hookFormErrorAdapter(form, f.fieldState)}>
                <SydInput
                  value={f.field.value}
                  onChange={f.field.onChange}
                  placeholder='e.g. nightly-ops'
                  disabled={isEdit}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={form.control}
            name='description'
            render={(f) => (
              <SydLabel label='Description'>
                <SydInput
                  value={f.field.value}
                  onChange={f.field.onChange}
                  placeholder='What is this identity used for?'
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            control={form.control}
            name='userId'
            rules={{
              validate: (v) => (v && v.value) ? true : 'User is required'
            }}
            render={(f) => (
              <SydLabel label='User' required error={hookFormErrorAdapter(form, f.fieldState)}>
                <SydAutocomplete
                  options={filteredUserOptions}
                  value={f.field.value}
                  onChange={(_, val) => f.field.onChange(val)}
                  onInputChange={(_, val) => setUserSearchText(val)}
                  placeholder='Search by name or user ID...'
                  isLoading={isLoadingUsers}
                  size='sm'
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            control={form.control}
            name='enabled'
            render={(f) => (
              <SydLabel label='Enabled'>
                <SydCheckbox
                  value={f.field.value}
                  onChange={(e) => f.field.onChange(e.target.checked)}
                  label='Identity is available for assignment'
                />
              </SydLabel>
            )}
          />
        </Grid>
      </Grid>
      {error ? <p style={{ color: 'var(--error)', marginTop: 8 }}>{error}</p> : null}
      <SydModalActions style={{ marginTop: '24px' }}>
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onCancel}>
          Cancel
        </SydButton>
        <SydButton
          processing={processing}
          variant='primary'
          size='md'
          onClick={onSubmit}
        >
          {isEdit ? 'Save Changes' : 'Create'}
        </SydButton>
      </SydModalActions>
    </>
  )
}

DelegatedIdentityForm.propTypes = {
  identityId: PropTypes.number,
  existing: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired
}

export default DelegatedIdentityForm
